import type { ZodType } from 'zod';

/**
 * localStorage 안전 파싱 헬퍼.
 *
 * 신뢰 경계 밖 저장소(사용자가 DevTools로 임의 조작 가능)를 다룰 때
 * `JSON.parse(raw) as Type` 패턴의 치명적 약점(throw, shape drift, 타입 단언)을 보완해
 * Zod 스키마로 runtime 검증 후 fallback 반환. all-or-nothing → graceful degrade.
 *
 * zustand persist store는 이 헬퍼 대신 자체 storage adapter(DI)를 사용할 것 (팀 정책).
 */

export function safeGet<T>(key: string, schema: ZodType<T>, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return fallback;
    const result = schema.safeParse(JSON.parse(raw));
    return result.success ? result.data : fallback;
  } catch {
    return fallback;
  }
}

export function safeSet(key: string, value: unknown): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // QuotaExceeded / private mode / 기타 storage error — silent fail
  }
}

export function safeRemove(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(key);
  } catch {
    // silent fail
  }
}

/**
 * raw JSON 문자열(예: StorageEvent.newValue, zustand persist adapter 입력값)을
 * Zod 스키마로 검증. `safeGet`과 달리 localStorage 직접 읽지 않음.
 */
export function safeParseStored<T>(raw: string, schema: ZodType<T>): T | null {
  try {
    const result = schema.safeParse(JSON.parse(raw));
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}
